import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getNPOSubscription, resumeSubscription, createCustomerPortalSession } from "@/lib/queries/subscriptions";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, XCircle, RefreshCw } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { PricingSection } from "@/components/home/PricingSection";
import SEO from '@/components/SEO';

export default function NPOSubscription({ profile, session }: { profile: any | null, session: any | null }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [isCancelling, setIsCancelling] = useState(false);
  const [isResuming, setIsResuming] = useState(false);

  const { data: subscription, isLoading } = useQuery({
    queryKey: ["subscription", profile?.id],
    queryFn: () => getNPOSubscription(profile?.id as string, session?.access_token as string),
    enabled: !!profile?.id,
  });

  const PLANS = [
    {
      id: "essential",
      name: "Cause Essential",
      product: import.meta.env.VITE_SUBSCRIPTION_CAUSE_STARTER_PRODUCT,
      interval: "monthly",
      amount: 4900,
      url: import.meta.env.VITE_SUBSCRIPTION_CAUSE_STARTER_MONTHLY
    },
    {
      id: "catalyst",
      name: "Cause Catalyst",
      product: import.meta.env.VITE_SUBSCRIPTION_CAUSE_CATALYST_PRODUCT,
      interval: "monthly",
      amount: 9900,
      url: import.meta.env.VITE_SUBSCRIPTION_CAUSE_CATALYST_MONTHLY
    },
    {
      id: "pro",
      name: "CausePro",
      product: import.meta.env.VITE_SUBSCRIPTION_CAUSE_PRO_PRODUCT,
      interval: "monthly",
      amount: 14900,
      url: import.meta.env.VITE_SUBSCRIPTION_CAUSE_PRO_MONTHLY
    },
    {
      id: "essential",
      name: "Cause Starter",
      product: import.meta.env.VITE_SUBSCRIPTION_CAUSE_STARTER_PRODUCT,
      interval: "annual",
      amount: 4100,
      url: import.meta.env.VITE_SUBSCRIPTION_CAUSE_STARTER_YEARLY
    },
    {
      id: "catalyst",
      name: "Cause Catalyst",
      product: import.meta.env.VITE_SUBSCRIPTION_CAUSE_CATALYST_PRODUCT,
      interval: "annual",
      amount: 8300,
      url: import.meta.env.VITE_SUBSCRIPTION_CAUSE_CATALYST_YEARLY
    },
    {
      id: "pro",
      name: "Cause Pro",
      product: import.meta.env.VITE_SUBSCRIPTION_CAUSE_PRO_PRODUCT,
      interval: "annual",
      amount: 12500,
      url: import.meta.env.VITE_SUBSCRIPTION_CAUSE_PRO_YEARLY
    },
  ]
  useEffect(() => {
    const plan = searchParams.get("plan");
    const interval = searchParams.get("interval") || "monthly";
    
    // Only redirect if we have loaded the subscription data and confirmed there isn't one
    if (plan && profile?.id && profile?.email && !isLoading && !subscription?.data?.status) {
      const selectedPlan = PLANS.find(p => 
        p.id === plan.toLowerCase() && 
        p.interval === (interval === 'annual' ? 'annual' : 'monthly')
      );

      if (selectedPlan?.url) {
        const url = new URL(selectedPlan.url);
        url.searchParams.append('prefilled_email', profile.email);
        url.searchParams.append('client_reference_id', profile.id);

        window.location.href = url.toString();
      }
    }
  }, [searchParams, profile, subscription?.data?.status, isLoading]);

  const formatDate = (timestamp: string | number | null | undefined): string => {
    if (!timestamp) return 'N/A';
    try {
      // Convert number to milliseconds if it's a Unix timestamp
      const date = typeof timestamp === 'number' ? new Date(timestamp * 1000) : new Date(timestamp);
      return format(date, "PPP");
    } catch (error) {
      console.error('Error formatting date:', error, 'timestamp:', timestamp);
      return 'Invalid date';
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setIsCancelling(true);
      window.open(await createCustomerPortalSession(session?.access_token), '_blank');
    } catch (error) {
      console.error('Error redirecting to portal:', error);
      toast({
        title: "Error",
        description: "Failed to open customer portal",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleResumeSubscription = async () => {
    if (!subscription?.data?.id) {
      toast({
        title: "Error",
        description: "No subscription ID found",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsResuming(true);
      const response = await resumeSubscription(subscription.data.id, session?.access_token);
      await queryClient.invalidateQueries(["subscription", profile?.id]);
      
      if (response.error) {
        throw new Error(response.error);
      }

      toast({
        title: "Success",
        description: "Your subscription has been resumed.",
      });
      
      // Refresh the subscription data
      await queryClient.invalidateQueries(["subscription", profile?.id]);
    } catch (error: any) {
      console.error('Error resuming subscription:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to resume subscription",
        variant: "destructive",
      });
    } finally {
      setIsResuming(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 space-y-8 p-8 pt-6">
        <div className="flex items-center justify-between">
          <h2 className="text-4xl font-bold tracking-tight">Subscription Management</h2>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center py-10">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            <span>Loading subscription details...</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="NPO Subscription" 
        description="Manage your NPO subscription and access premium features to enhance your volunteer program management."
      />
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Subscription Management</h1>
        </div>
        {subscription?.data && subscription.data.plan ? (
          <Card>
            <CardHeader>
              <CardTitle>Current Subscription</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-lg font-medium flex items-center gap-2">
                  Status:{' '}
                  {subscription.data.status === 'trialing' ? (
                    <span className="inline-flex items-center rounded-md bg-blue-400/10 px-2 py-1 text-xs font-medium text-blue-400 ring-1 ring-inset ring-blue-400/20">
                      On Trial
                    </span>
                  ) : subscription.data.status === 'active' ? (
                    <span className="inline-flex items-center rounded-md bg-green-400/10 px-2 py-1 text-xs font-medium text-green-400 ring-1 ring-inset ring-green-400/20">
                      Subscribed
                    </span>
                  ) : (
                    <span className="capitalize">{subscription.data.status || 'Unknown'}</span>
                  )}
                  {subscription.data.cancel_at_period_end && (
                    <span className="inline-flex items-center rounded-md bg-destructive/10 px-2 py-1 text-xs font-medium text-destructive ring-1 ring-inset ring-destructive/20">
                      Cancels at period end
                    </span>
                  )}
                  {(isCancelling || isResuming) && (
                    <span className="inline-flex items-center text-sm text-muted-foreground">
                      <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      {isCancelling ? "Cancelling..." : "Resuming..."}
                    </span>
                  )}
                </p>
                <p className="text-gray-600">
                  Plan: <span className="capitalize">
                    {(() => {
                      const currentPlan = PLANS.find(p => 
                        p.product === subscription.data.plan.product && 
                        p.interval === (subscription.data.plan.interval == 'year' ? 'annual' : 'monthly')
                      );
                      return currentPlan ? 
                        `${currentPlan.name} ($${(subscription.data?.plan?.amount / 100).toFixed(2)}/${subscription.data.plan.interval})` :
                        `$${(subscription.data?.plan?.amount / 100).toFixed(2)}/${subscription.data.plan.interval}`;
                    })()}
                  </span>
                </p>
              </div>
              <div className="space-y-2">
                {subscription.data.status === 'trialing' && subscription.data.plan.trial_period_days ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Your {subscription.data.plan.trial_period_days}-day trial is active
                    </p>
                  </>
                ) : null}
                {subscription.data.canceled_at && (
                  <p className="text-gray-600">
                    Cancelled on: {formatDate(subscription.data.canceled_at)}
                  </p>
                )}
                {subscription.data.cancel_at && (
                  <p className="text-sm text-destructive font-medium">
                    Your subscription will end on {formatDate(subscription.data.cancel_at)}
                  </p>
                )}
              </div>
              <div className="flex gap-4 pt-4">
                {!subscription.data.cancel_at_period_end && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" disabled={isCancelling}>
                        {isCancelling ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Cancelling...
                          </>
                        ) : (
                          <>
                            <XCircle className="mr-2 h-4 w-4" />
                            Cancel Subscription
                          </>
                        )}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you sure you want to cancel?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Your subscription will be cancelled at the end of the current billing period. You will continue to have access to premium features until then.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleCancelSubscription}
                          disabled={isCancelling}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          {isCancelling ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Cancelling...
                            </>
                          ) : (
                            <>
                              <XCircle className="mr-2 h-4 w-4" />
                              Yes, Cancel Subscription
                            </>
                          )}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                {subscription.data.cancel_at_period_end && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button disabled={isResuming}>
                        {isResuming ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Resuming...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Resume Subscription
                          </>
                        )}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Resume Subscription</AlertDialogTitle>
                        <AlertDialogDescription>
                          Your subscription will continue at ${(subscription.data.plan.amount / 100).toFixed(2)} per {subscription.data.plan.interval}.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleResumeSubscription}
                          disabled={isResuming}
                        >
                          {isResuming ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Resuming...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="mr-2 h-4 w-4" />
                              Yes, Resume Subscription
                            </>
                          )}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <PricingSection />
        )}
      </div>
    </>
  );
}